import { useState, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { draftQueries } from '@/lib/api/queries/draft'
import {
  useApproveDraft, useRejectDraft, useRedraftDraft,
  useUpdateDraft, useSchedulePublish, useRepublishDraft,
} from '@/lib/api/mutations/draft'
import { draftEditSchema, type DraftEditFormData } from '@/lib/constants/draft'
import { insertImageAtCursor, replaceInlineImage } from '@/lib/utils/markdown-image-utils'
import { DraftStickyHeader } from './draft-sticky-header'
import { DraftBodyViewer } from './draft-body-viewer'
import { DraftBodyEditor } from './draft-body-editor'
import { DraftSidebar } from './draft-sidebar'
import { DraftSourcesCard } from './draft-sources-card'
import { DraftSeoEditor } from './draft-seo-editor'
import { DraftDetailSkeleton } from './draft-detail-skeleton'
import { ApproveDialog } from './approve-dialog'
import { RejectDialog } from './reject-dialog'
import { SchedulePublishPicker } from './schedule-publish-picker'
import { ImageReplacementModal } from './image-replacement-modal'
import { HeroImageEditable } from './hero-image-editable'
import { ImageCandidatesGrid } from './image-candidates-grid'
import { AltTextInput } from './alt-text-input'
import { ShareDraftButton } from './share-draft-button'
import { DraftChatPanel } from './draft-chat-panel'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'

interface DraftDetailPageProps {
  agentId: string
  draftId: string
}

type ImageModalContext = 'hero' | { type: 'inline'; oldUrl?: string; oldAlt?: string }

export function DraftDetailPage({ agentId, draftId }: DraftDetailPageProps) {
  const { data: draft, isLoading, isError } = useQuery({
    ...draftQueries.detail(agentId, draftId),
    refetchInterval: (query) => {
      const ps = query.state.data?.publishStatus
      return ps === 'pending' || ps === 'processing' ? 3000 : false
    },
  })
  const [isEditing, setIsEditing] = useState(false)
  const [showApproveDialog, setShowApproveDialog] = useState(false)
  const [showRejectDialog, setShowRejectDialog] = useState(false)
  const [showImageModal, setShowImageModal] = useState(false)
  const [imageModalContext, setImageModalContext] = useState<ImageModalContext>('hero')
  const [cursorPos, setCursorPos] = useState(0)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const [revertDialog, setRevertDialog] = useState<
    | { open: true; message: string; onConfirm: () => void }
    | { open: false }
  >({ open: false })

  const confirmRevert = (message: string, onConfirm: () => void) =>
    setRevertDialog({ open: true, message, onConfirm })

  // Mutation hooks
  const approveMutation = useApproveDraft(agentId, draftId)
  const rejectMutation = useRejectDraft(agentId, draftId)
  const redraftMutation = useRedraftDraft(agentId, draftId)
  const updateMutation = useUpdateDraft(agentId, draftId)
  const scheduleMutation = useSchedulePublish(agentId, draftId)
  const republishMutation = useRepublishDraft(agentId, draftId)

  // Edit form
  const form = useForm<DraftEditFormData>({
    resolver: zodResolver(draftEditSchema),
    defaultValues: {
      title: draft?.title ?? '',
      body: draft?.body ?? '',
      heroImageUrl: draft?.heroImageUrl ?? draft?.selectedImageUrl ?? null,
      heroImageAlt: draft?.heroImageAlt ?? '',
      seo: {
        title: draft?.seo?.title ?? '',
        description: draft?.seo?.description ?? '',
        keywords: draft?.seo?.keywords ?? [],
      },
    },
  })

  // Handlers
  const handleStartEdit = () => {
    if (!draft) return
    form.reset({
      title: draft.title,
      body: draft.body,
      heroImageUrl: draft.heroImageUrl ?? draft.selectedImageUrl ?? null,
      heroImageAlt: draft.heroImageAlt ?? '',
      seo: { title: draft.seo?.title ?? '', description: draft.seo?.description ?? '', keywords: draft.seo?.keywords ?? [] },
    })
    setIsEditing(true)
  }

  const handleSave = () => {
    form.handleSubmit((data) => {
      const wasPublished = draft?.status === 'published'
      updateMutation.mutate(data, {
        onSuccess: () => {
          if (wasPublished) {
            // Flip status back to draft — requires re-approve + re-publish
            redraftMutation.mutate(undefined, {
              onSuccess: () => setIsEditing(false),
            })
          } else {
            setIsEditing(false)
          }
        },
      })
    })()
  }

  const handleDiscard = () => {
    if (form.formState.isDirty) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to discard them?')) return
    }
    form.reset()
    setIsEditing(false)
  }

  const handleApprove = (scheduledAt?: string) => {
    approveMutation.mutate(scheduledAt ? { scheduledAt } : undefined, {
      onSuccess: () => setShowApproveDialog(false),
    })
  }

  const handleReject = (reason: string) => {
    rejectMutation.mutate({ reason }, {
      onSuccess: () => setShowRejectDialog(false),
    })
  }

  const handleRedraft = () => {
    redraftMutation.mutate()
  }

  // Gate for image mutations on published posts. If the draft is already editable,
  // runs the action immediately. If published, prompts the user, reverts to draft
  // via redraftMutation, then runs the action. Same pattern as handleSave() for text edits.
  const requireDraftStatus = (action: () => void) => {
    if (!draft) return
    if (draft.status !== 'published') {
      action()
      return
    }
    confirmRevert(
      'Changing images on this published post will revert it to draft. You will need to approve and publish it again to make changes live. Continue?',
      () => redraftMutation.mutate(undefined, { onSuccess: () => action() }),
    )
  }

  const handleSchedule = (date: string) => {
    scheduleMutation.mutate({ scheduledAt: date })
  }

  const handleRepublish = () => {
    republishMutation.mutate()
  }

  // Image modal handlers
  const handleOpenHeroImageModal = () => {
    setImageModalContext('hero')
    setShowImageModal(true)
  }

  const handleOpenInlineImageModal = () => {
    setCursorPos(textareaRef.current?.selectionStart ?? 0)
    setImageModalContext({ type: 'inline' })
    setShowImageModal(true)
  }

  const handleImageSelected = (imageUrl: string, altText: string) => {
    if (imageModalContext === 'hero') {
      form.setValue('heroImageUrl', imageUrl, { shouldDirty: true })
      form.setValue('heroImageAlt', altText, { shouldDirty: true })
    } else if (imageModalContext.type === 'inline') {
      const currentBody = form.getValues('body')
      let newBody: string
      if (imageModalContext.oldUrl) {
        newBody = replaceInlineImage(currentBody, imageModalContext.oldUrl, imageUrl, altText)
      } else {
        newBody = insertImageAtCursor(currentBody, cursorPos, imageUrl, altText)
      }
      form.setValue('body', newBody, { shouldDirty: true })
    }
    setShowImageModal(false)
  }

  if (isLoading) return <DraftDetailSkeleton />

  if (isError || !draft) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <p className="text-destructive mb-4">Unable to load this draft. Please go back and try again.</p>
      </div>
    )
  }

  // Action buttons for view mode
  const viewActions = (
    <>
      <ShareDraftButton draft={draft} agentId={agentId} draftId={draftId} />
      {(draft.publishStatus === 'failed' || draft.publishStatus === 'succeeded') &&
        (draft.status === 'approved' || draft.status === 'published') && (
        <Button variant="outline" onClick={handleRepublish} disabled={republishMutation.isPending}>
          {republishMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Republish
        </Button>
      )}
      {draft.status === 'draft' && (
        <>
          <Button onClick={() => setShowApproveDialog(true)}>Approve Draft</Button>
          <Button variant="outline" className="text-destructive" onClick={() => setShowRejectDialog(true)}>
            Reject Draft
          </Button>
          <Button variant="outline" onClick={handleStartEdit}>Edit Draft</Button>
        </>
      )}
      {draft.status === 'rejected' && (
        <Button variant="outline" onClick={handleRedraft} disabled={redraftMutation.isPending}>
          {redraftMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Return to Draft
        </Button>
      )}
      {draft.status === 'approved' && (
        <SchedulePublishPicker onSchedule={handleSchedule} isPending={scheduleMutation.isPending} />
      )}
      {draft.status === 'published' && (
        <Button
          variant="outline"
          onClick={() =>
            confirmRevert(
              'Editing this published post will revert it to draft. You will need to approve and publish it again to make changes live. Continue?',
              handleStartEdit,
            )
          }
        >
          Edit Post
        </Button>
      )}
    </>
  )

  // Action buttons for edit mode
  const isSavingEdit = updateMutation.isPending || redraftMutation.isPending
  const editActions = (
    <>
      <ShareDraftButton draft={draft} agentId={agentId} draftId={draftId} />
      <Button onClick={handleSave} disabled={isSavingEdit}>
        {isSavingEdit && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Save Changes
      </Button>
      <Button variant="outline" onClick={handleDiscard} disabled={isSavingEdit}>
        Discard Changes
      </Button>
    </>
  )

  return (
    <div className="h-full flex flex-col">
      <DraftStickyHeader
        draft={draft}
        agentId={agentId}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        editActions={editActions}
        viewActions={viewActions}
      />

      {/* Rejection reason callout */}
      {draft.status === 'rejected' && draft.rejectionReason && (
        <div className="bg-destructive/5 border border-destructive/20 rounded-lg mx-4 md:mx-8 mt-4 p-4">
          <p className="text-sm font-medium text-destructive mb-1">Rejection reason</p>
          <p className="text-sm text-muted-foreground">{draft.rejectionReason}</p>
        </div>
      )}

      {/* Two-column layout */}
      <div className="flex-1 overflow-auto p-4 md:p-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            {isEditing ? (
              <DraftBodyEditor
                register={form.register}
                watch={form.watch}
                error={form.formState.errors.body?.message}
                onInsertImage={handleOpenInlineImageModal}
                textareaRef={textareaRef}
              />
            ) : (
              <>
                <DraftBodyViewer body={draft.body} />
                <DraftSourcesCard sources={draft.sources} />
              </>
            )}
          </div>
          <div className="md:col-span-1">
            {isEditing ? (
              <div className="space-y-4">
                <HeroImageEditable
                  url={form.watch('heroImageUrl') ?? null}
                  alt={form.watch('heroImageAlt') ?? null}
                  onReplace={handleOpenHeroImageModal}
                />
                <AltTextInput
                  value={form.watch('heroImageAlt') ?? ''}
                  onChange={(val) => form.setValue('heroImageAlt', val, { shouldDirty: true })}
                />
                <ImageCandidatesGrid
                  agentId={agentId}
                  draftId={draftId}
                  draftTitle={draft.title}
                  requireDraftStatus={requireDraftStatus}
                  imageOpsPending={redraftMutation.isPending}
                />
                <DraftSeoEditor
                  register={form.register}
                  control={form.control}
                  errors={form.formState.errors}
                />
              </div>
            ) : (
              <DraftSidebar
                draft={draft}
                agentId={agentId}
                draftId={draftId}
                requireDraftStatus={requireDraftStatus}
                imageOpsPending={redraftMutation.isPending}
              />
            )}
          </div>
          {/* Phase 35-02 (DRAFT-01): agent chat panel — sibling to the body editor */}
          <div className="md:col-span-3">
            <DraftChatPanel agentId={agentId} draftId={draftId} />
          </div>
        </div>
      </div>

      {/* Dialogs */}
      <ApproveDialog
        open={showApproveDialog}
        onOpenChange={setShowApproveDialog}
        onConfirm={handleApprove}
        isPending={approveMutation.isPending}
      />
      <RejectDialog
        open={showRejectDialog}
        onOpenChange={setShowRejectDialog}
        onConfirm={handleReject}
        isPending={rejectMutation.isPending}
      />
      <ImageReplacementModal
        open={showImageModal}
        onOpenChange={setShowImageModal}
        onImageSelected={handleImageSelected}
        initialAltText={imageModalContext === 'hero' ? (form.watch('heroImageAlt') ?? '') : (typeof imageModalContext === 'object' ? imageModalContext.oldAlt ?? '' : '')}
        agentId={agentId}
        draftId={draftId}
      />
      <AlertDialog
        open={revertDialog.open}
        onOpenChange={(open) => {
          if (!open) setRevertDialog({ open: false })
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revert to draft?</AlertDialogTitle>
            <AlertDialogDescription>
              {revertDialog.open ? revertDialog.message : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (revertDialog.open) {
                  const fn = revertDialog.onConfirm
                  setRevertDialog({ open: false })
                  fn()
                }
              }}
            >
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
